import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, supabaseEnabled } from '../lib/supabase';

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = useState({}); // { key: value }
  const [loaded, setLoaded] = useState(false);
  const [session, setSession] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Load all published content once.
  useEffect(() => {
    if (!supabaseEnabled) { setLoaded(true); return; }
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from('site_content')
        .select('key, value');
      if (active) {
        if (!error && data) {
          const map = {};
          data.forEach((row) => { map[row.key] = row.value; });
          setContent(map);
        }
        setLoaded(true);
      }
    })();
    return () => { active = false; };
  }, []);

  // Track auth session (owner login).
  useEffect(() => {
    if (!supabaseEnabled) return;
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) setEditMode(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Read a value with a code-level fallback (the original hardcoded text).
  const get = useCallback(
    (key, fallback = '') => {
      const v = content[key];
      return v === undefined || v === null || v === '' ? fallback : v;
    },
    [content]
  );

  // Save a single field (owner only).
  const save = useCallback(async (key, value, meta = {}) => {
    setContent((prev) => ({ ...prev, [key]: value })); // optimistic
    if (!supabaseEnabled) return { error: null };
    const { error } = await supabase
      .from('site_content')
      .upsert({ key, value, page: meta.page || null, label: meta.label || null, type: meta.type || 'text' });
    return { error };
  }, []);

  const value = {
    get,
    save,
    loaded,
    isOwner: Boolean(session),
    session,
    editMode,
    setEditMode,
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}
